import { api } from './api'

export type PlanTier = 'starter' | 'growth' | 'enterprise'
export type BillingInterval = 'month' | 'year'

export type PublicPlan = {
  id: string
  name: string
  tier: string | null
  interval: string
  priceId: string
  creditsPerMonth: number
  rolloverCap: number
  features: Record<string, boolean>
}

export type BillingStatus = {
  planTier: string | null
  planResolved: boolean
  subscriptionStatus: string | null
  hasSubscription: boolean
  hasStripeCustomer: boolean
  creditBalance: number
  onTrialCredits: boolean
  // True only for a self-serve account with no active subscription that
  // hasn't yet confirmed its email — the dashboard paywall must not appear
  // for these users until they do (or don't) confirm.
  trialGrantPending: boolean
}

// Stashed before an OAuth redirect so the post-signup checkout can resume
// with the plan the user picked.
export const PENDING_PLAN_KEY = 'lensora_pending_plan'

export function stashPendingPlan(tier: PlanTier, interval: BillingInterval): void {
  localStorage.setItem(PENDING_PLAN_KEY, JSON.stringify({ tier, interval }))
}

export function popPendingPlan(): { tier: PlanTier; interval: BillingInterval } | null {
  const raw = localStorage.getItem(PENDING_PLAN_KEY)
  if (!raw) return null
  localStorage.removeItem(PENDING_PLAN_KEY)
  try {
    return JSON.parse(raw) as { tier: PlanTier; interval: BillingInterval }
  } catch {
    return null
  }
}

export function fetchPlans(): Promise<PublicPlan[]> {
  return api.get<PublicPlan[]>('/api/billing/plans')
}

export function fetchBillingStatus(): Promise<BillingStatus> {
  return api.get<BillingStatus>('/api/billing/status')
}

function checkoutUrls() {
  const base = window.location.origin
  return {
    successUrl: `${base}/dashboard/settings/billing?checkout=success`,
    cancelUrl: `${base}/dashboard/settings/billing?checkout=cancelled`,
  }
}

// Creates a Checkout session and redirects the browser to Stripe.
export async function startCheckout(priceId: string): Promise<void> {
  const { url } = await api.post<{ url: string }>('/api/billing/checkout', {
    priceId,
    ...checkoutUrls(),
  })
  window.location.href = url
}

// Resolves the tier+interval to a plan price, then redirects to Checkout.
// Falls back to the tier's monthly price when the requested interval isn't
// configured. No longer called straight after signup — kept for tier-targeted
// upgrade entry points (e.g. an "Upgrade to Growth" CTA on a gated feature).
export async function startCheckoutByTier(
  tier: PlanTier,
  interval: BillingInterval = 'month'
): Promise<void> {
  const plans = await fetchPlans()
  const plan =
    plans.find((p) => p.tier === tier && p.interval === interval) ??
    plans.find((p) => p.tier === tier)
  if (!plan) throw new Error(`No plan configured for tier "${tier}"`)
  await startCheckout(plan.priceId)
}
